import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Building2, MapPin, Image, Users, Utensils, LogOut, QrCode, BookOpen } from "lucide-react";
import { api, getAdminToken, setAdminToken } from "../../api";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/admin/musei", label: "Musei", icon: Building2, testId: "nav-musei" },
  { to: "/admin/citta", label: "Città", icon: MapPin, testId: "nav-citta" },
  { to: "/admin/opere", label: "Opere", icon: Image, testId: "nav-opere" },
  { to: "/admin/luoghi", label: "Luoghi", icon: Utensils, testId: "nav-luoghi" },
  { to: "/admin/utenti", label: "Utenti", icon: Users, testId: "nav-utenti" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) return;
    api.adminMe().then(setMe).catch(() => {
      setAdminToken(null);
      navigate("/admin/login");
    });
  }, [navigate]);

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />;

  function logout() {
    setAdminToken(null);
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen flex bg-bg">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0B0F1A] text-white flex flex-col p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center">
            <QrCode size={20} strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-heading font-bold text-lg leading-tight">QrTourist</div>
            <div className="text-[10px] tracking-widest text-white/50">ADMIN</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive ? "bg-brand text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 pt-4 mt-4">
          <a
            href={`${BACKEND_URL}/api/admin/guide.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="admin-guide-pdf"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition mb-2"
          >
            <BookOpen size={18} /> Guida PDF
          </a>
          {me && (
            <div className="px-2 mb-3">
              <div className="text-sm font-semibold text-white truncate">{me.email}</div>
              <div className="text-[10px] tracking-widest text-white/50">{me.role?.toUpperCase()}</div>
            </div>
          )}
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-red-300 transition"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
